import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { getCategory } from '../../utils/contentCategories';

/**
 * Lightbox preview for a single media asset. Click backdrop or press Esc to close.
 * Images render unscaled (max-fit to viewport). Videos get native controls.
 */
const AssetPreviewModal = ({ item, onClose }) => {
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) return null;

  const isVideo = String(item.type || '').toLowerCase().startsWith('video')
    || String(item.section || '').toLowerCase() === 'video';
  const cat = item.category ? getCategory(item.category) : null;
  const chipColor = cat ? cat.color : '#9ca3af';
  const chipLabel = cat ? cat.label : 'Uncategorized';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 32,
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        style={{
          position: 'absolute', top: 16, right: 16,
          background: 'rgba(255,255,255,0.12)', color: '#fff',
          border: 'none', borderRadius: '50%',
          width: 40, height: 40, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <X size={20} />
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 'min(1200px, 95vw)', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center',
        }}
      >
        {item.url && isVideo ? (
          <video
            src={item.url}
            controls
            autoPlay
            playsInline
            style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, background: '#000' }}
          />
        ) : item.url ? (
          <img
            src={item.url}
            alt={item.title || ''}
            style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, objectFit: 'contain' }}
          />
        ) : (
          <div style={{ color: '#fff', padding: 40 }}>No preview available.</div>
        )}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          color: '#fff', fontSize: 14, padding: '4px 12px',
          maxWidth: '100%',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 600,
            color: chipColor, background: `${chipColor}25`,
            padding: '3px 8px', borderRadius: 4, flexShrink: 0,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: chipColor }} />
            {chipLabel}
          </span>
          <span style={{
            fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.title || 'Untitled'}
          </span>
          {item.width && item.height && (
            <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
              {item.width}×{item.height}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetPreviewModal;
